import APIService from 'services/APIService';
import Battle, { BattleDTO, BattleStatus } from '../../models/battles/Battle';
import BattlePlayerStatus, {
	BattlePlayerStatusDTO,
	BattlePlayerStatusOption,
} from '../../models/battles/BattlePlayerStatus';
import moment from 'moment';
import HallOfFame, { HallOfFameDTO } from '../../models/battles/HallOfFame';

export type AcceptBattleAction = 'accept' | 'reject' | 'cancel';

const battlePlayerStatusOptionToUrl = {
	[BattlePlayerStatusOption.AVAILABLE]: '/teambooster/battles/battles-players/activate',
	[BattlePlayerStatusOption.NOT_AVAILABLE]: '/teambooster/battles/battles-players/deactivate-indefinitely',
	[BattlePlayerStatusOption.INACTIVE_FOR_24H]: '/teambooster/battles/battles-players/deactivate',
	[BattlePlayerStatusOption.INACTIVE_FOR_7D]: '/teambooster/battles/battles-players/deactivate',
};

export enum HallOfFameDateLimit {
	ONE_WEEK = '1 Week',
	ONE_MONTH = '1 Month',
}

export default class BattlesService {
	constructor(private readonly ApiService: APIService) {}

	fetchBattleList(limit: number, page: number = 1, statuses?: BattleStatus[]) {
		return this.ApiService.get<{
			items: BattleDTO[];
			count: number;
			waiting?: number;
			ongoing?: number;
			to_accept?: number;
		}>(
			`/teambooster/battles/my-battles`,
			{ limit, page, statuses: !!statuses?.length ? statuses.join(',') : undefined },
			undefined,
			{
				version: 'v2',
				controller: 'workflows',
			}
		).then(response => ({
			...response,
			items: response.items.map(battleDTO => this.toModel(battleDTO)),
		}));
	}

	fetchBattleGroupList() {
		return this.ApiService.get<{
			items: { id: string; name: string }[];
			count: number;
		}>('/teambooster/battles/battles-group/available', undefined, undefined, {
			version: 'v2',
			controller: 'workflows',
		});
	}

	fetchBattle(battleId: string): Promise<Battle> {
		return this.ApiService.get<BattleDTO>(`/teambooster/battles/battle/${battleId}`, undefined, undefined, {
			version: 'v2',
			controller: 'workflows',
		}).then(response => this.toModel(response));
	}

	toModel(battleDTO: BattleDTO): Battle {
		return new Battle(battleDTO);
	}

	acceptBattle(battleId: string, action: AcceptBattleAction) {
		return this.ApiService.post(`/teambooster/battles/battle/${battleId}/${action}`, undefined, undefined, {
			version: 'v2',
			controller: 'workflows',
		});
	}

	startNewDuel(group: string) {
		return this.ApiService.post(
			`/teambooster/battles/start`,
			{
				battles_group_id: group,
			},
			undefined,
			{
				version: 'v2',
				controller: 'workflows',
				omitErrorHandlers: true,
			}
		);
	}

	fetchMyStatus(): Promise<BattlePlayerStatus> {
		return this.ApiService.get<BattlePlayerStatusDTO>(
			`/teambooster/battles/battles-players/me`,
			undefined,
			undefined,
			{
				version: 'v2',
				controller: 'workflows',
			}
		).then(response => new BattlePlayerStatus(response));
	}

	updateMyStatus(option: BattlePlayerStatusOption): Promise<void> {
		const inactiveUntil =
			option === BattlePlayerStatusOption.INACTIVE_FOR_24H
				? moment().add({ hours: 24 })
				: option === BattlePlayerStatusOption.INACTIVE_FOR_7D
				? moment().add({ days: 7 })
				: null;

		return this.ApiService.patch<void>(
			battlePlayerStatusOptionToUrl[option],
			!!inactiveUntil ? { inactive_until: inactiveUntil } : undefined,
			undefined,
			{
				version: 'v2',
				controller: 'workflows',
			}
		);
	}

	fetchHallOfFame(limit: HallOfFameDateLimit): Promise<HallOfFame> {
		return this.ApiService.get<HallOfFameDTO>(
			`/teambooster/battles/halloffame?dateLimit=${limit}`,
			undefined,
			undefined,
			{
				version: 'v2',
				controller: 'workflows',
			}
		).then(response => new HallOfFame(response));
	}
}
